import { isRecord } from '@fruitful/fruitils';
import {
    type AiAgentId,
    BINARY_THREAD_SCORER_IDS,
    CMM_BRANCH_METRIC_ID,
    CONVERSATIONAL_MM_AGENT,
    ERRORS_METRIC_ID,
    getBaseResourceId,
    getThreadMetricDisplayName,
    getThreadMetricFieldIdsForAgent,
    getThreadMetricsLastCapturedAt,
    getThreadScorerIdsForAgent,
    getThreadScorersLastScoredAt,
    SPROUT_AGENT,
    threadNeedsEval,
} from '@fruitful/llm-service-shared';
import { CMMBranch } from '@fruitful/types';
import { type ListMemoryThreadsResponse } from '@mastra/client-js';
import { type Href, useRouter } from 'expo-router';
import { FilterMatchMode } from 'primereact/api';
import { Column } from 'primereact/column';
import {
    DataTable,
    type DataTableFilterMeta,
    type DataTableRowClickEvent,
} from 'primereact/datatable';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import {
    BadgeLabel,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ExternalLinkIcon,
    H6Emphasized,
    IconButton,
    Input,
    RefreshCwIcon,
    SearchIcon,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { ColumnDropdownFilter } from '../../common/components/ColumnFilters';
import { MutedTableCell } from '../../common/components/GridCellRenderers';
import { formatToSimpleDateTimeString } from '../../common/utils/formatting';
import { logRocketSessionsUrl } from '../../common/utils/LinkUtils';
import { IS_PRODUCTION, IS_STAGING } from '../../config/FruitfulEnvironment';
import { BRANCH_LABELS } from '../conversational-mm/qaSimulationThreads';
import { useBulkEvalAgentThreads } from '../hooks/useBulkEvalAgentThreads';
import { getScorerDisplayName, useListScorers } from '../hooks/useListScorers';
import { useMastraThreadStats } from '../hooks/useMastraThreadStats';
import { formatScorerValue, getScorerValueColor } from '../utils/scoreUtils';
import {
    formatThreadMetricFieldValue,
    getThreadHasError,
    getThreadMetricValue,
    getThreadScorerResult,
} from '../utils/threadMetadataUtils';
import { ScorerValueWithRubric } from './ScorerValueWithRubric';

const showExternalObservabilityColumns = IS_STAGING || IS_PRODUCTION;

const QA_DASHBOARD_PATH_BY_AGENT_ID: Partial<Record<AiAgentId, Href>> = {
    [CONVERSATIONAL_MM_AGENT.id]: '/conversational-onboarding-qa',
    [SPROUT_AGENT.id]: '/sprout-qa',
};

type MastraMemoryThread = ListMemoryThreadsResponse['threads'][number];

type BinaryScorerFilterValue = 'Pass' | 'Fail';

const BINARY_SCORER_FILTER_OPTIONS: Array<{
    label: string;
    value: BinaryScorerFilterValue | null;
}> = [
    { label: 'All', value: null },
    { label: 'Pass', value: 'Pass' },
    { label: 'Fail', value: 'Fail' },
];

type MetricColumnFilterValue = boolean | CMMBranch;

const HAS_ERROR_FILTER_OPTIONS: Array<{
    label: string;
    value: MetricColumnFilterValue | null;
}> = [
    { label: 'All', value: null },
    { label: 'Yes', value: true },
    { label: 'No', value: false },
];

const isBinaryThreadScorer = (scorerId: string): boolean =>
    (BINARY_THREAD_SCORER_IDS as readonly string[]).includes(scorerId);

const BRANCH_FILTER_OPTIONS: Array<{
    label: string;
    value: MetricColumnFilterValue | null;
}> = [
    { label: 'All', value: null },
    ...Object.values(CMMBranch).map((branch) => ({
        label: BRANCH_LABELS[branch] ?? branch,
        value: branch,
    })),
];

const getMetricColumnFilterOptions = (
    metricFieldId: string,
): Array<{ label: string; value: MetricColumnFilterValue | null }> | null => {
    if (metricFieldId === CMM_BRANCH_METRIC_ID) {
        return BRANCH_FILTER_OPTIONS;
    }

    if (metricFieldId === ERRORS_METRIC_ID) {
        return HAS_ERROR_FILTER_OPTIONS;
    }

    return null;
};

const hasThreadId = (value: unknown): value is { id: string } => {
    if (value == null || typeof value !== 'object') {
        return false;
    }

    return 'id' in value && typeof value.id === 'string';
};

const setNestedPath = (
    target: Record<string, unknown>,
    path: string,
    value: unknown,
): void => {
    const segments = path.split('.');
    let current: Record<string, unknown> = target;

    for (let index = 0; index < segments.length - 1; index += 1) {
        const segment = segments[index];
        if (segment == null) {
            return;
        }

        const existing = current[segment];
        if (!isRecord(existing)) {
            const next: Record<string, unknown> = {};
            current[segment] = next;
            current = next;
            continue;
        }

        current = existing;
    }

    const leaf = segments[segments.length - 1];
    if (leaf != null) {
        current[leaf] = value;
    }
};

const toBinaryScorerFilterValue = (
    score: number | null,
): BinaryScorerFilterValue | null => {
    if (score == null) {
        return null;
    }

    return score >= 1 ? 'Pass' : 'Fail';
};

interface AiDashboardThreadRow {
    id: string;
    resourceId: string;
    membershipId: string | null;
    metricsCapturedAt: string | null;
    scoredAt: string | null;
    updatedAt: string;
    metrics: Record<string, unknown>;
    scorers: Record<string, number | BinaryScorerFilterValue | null>;
    thread: MastraMemoryThread;
}

const buildThreadRow = ({
    thread,
    agentId,
    metricFieldIds,
    scorerIds,
}: {
    thread: MastraMemoryThread;
    agentId: AiAgentId;
    metricFieldIds: readonly string[];
    scorerIds: readonly string[];
}): AiDashboardThreadRow => {
    const metricsCapturedAt = getThreadMetricsLastCapturedAt(thread.metadata);
    const scoredAt = getThreadScorersLastScoredAt(thread.metadata);
    const metrics: Record<string, unknown> = {};
    const scorers: Record<string, number | BinaryScorerFilterValue | null> = {};

    for (const metricFieldId of metricFieldIds) {
        const value =
            metricFieldId === ERRORS_METRIC_ID
                ? getThreadHasError(thread.metadata)
                : getThreadMetricValue(thread.metadata, metricFieldId);
        setNestedPath(metrics, metricFieldId, value);
    }

    for (const scorerId of scorerIds) {
        const score =
            getThreadScorerResult(thread.metadata, scorerId)?.score ?? null;
        scorers[scorerId] = isBinaryThreadScorer(scorerId)
            ? toBinaryScorerFilterValue(score)
            : score;
    }

    return {
        id: thread.id,
        // Base resource ID is the visitorId / userId (agent prefix stripped).
        resourceId: getBaseResourceId(thread.resourceId, agentId),
        // Populated from the backend batch stats query, merged in `rows` below.
        membershipId: null,
        metricsCapturedAt,
        scoredAt,
        updatedAt: thread.updatedAt,
        metrics,
        scorers,
        thread,
    };
};

const createResourceIdFilterMeta = (
    resourceId: string | null,
): DataTableFilterMeta => ({
    resourceId: {
        value: resourceId,
        matchMode: FilterMatchMode.EQUALS,
    },
});

const createInitialFilterMeta = (
    resourceId: string | null,
): DataTableFilterMeta => ({
    // Single search box across thread / resource / membership IDs.
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    ...createResourceIdFilterMeta(resourceId),
});

const useThreadTableFilters = (resourceId: string | null) => {
    const [filters, setFilters] = useState<DataTableFilterMeta>(() =>
        createInitialFilterMeta(resourceId),
    );
    const [globalSearch, setGlobalSearch] = useState('');

    // URL `resourceId` seeds the table filter. Edits in the filter UI stay local
    // and are not written back to the query string.
    useEffect(() => {
        setFilters((current) => ({
            ...current,
            ...createResourceIdFilterMeta(resourceId),
        }));
    }, [resourceId]);

    const onGlobalSearchChange = (value: string) => {
        setGlobalSearch(value);
        const trimmed = value.trim();
        setFilters((current) => ({
            ...current,
            global: {
                value: trimmed === '' ? null : trimmed,
                matchMode: FilterMatchMode.CONTAINS,
            },
        }));
    };

    return {
        filters,
        globalSearch,
        onGlobalSearchChange,
        onFilter: (event: { filters: DataTableFilterMeta }) => {
            setFilters(event.filters);
        },
    };
};

export const AiDashboardThreadsTable = ({
    threads,
    agentId,
    resourceId,
    isRefreshing,
    onRefresh,
}: {
    threads: MastraMemoryThread[];
    agentId: AiAgentId;
    resourceId: string | null;
    isRefreshing: boolean;
    onRefresh: () => void;
}) => {
    const router = useRouter();
    const metricFieldIds = getThreadMetricFieldIdsForAgent(agentId);
    const scorerIds = getThreadScorerIdsForAgent(agentId);
    const showLogRocketColumn =
        showExternalObservabilityColumns && agentId !== SPROUT_AGENT.id; // Sprout `resourceId` is Stytch, not Fruitful `user.id`. Identity lookup lives on the thread detail page so this table does not N+1 admin fetches.
    const { data: scorerDisplayNames } = useListScorers();
    const qaDashboardPath = QA_DASHBOARD_PATH_BY_AGENT_ID[agentId];
    const { mutate: bulkEval, isPending: isBulkEvalPending } =
        useBulkEvalAgentThreads();
    const { filters, globalSearch, onGlobalSearchChange, onFilter } =
        useThreadTableFilters(resourceId);
    const unevaluatedCount = threads.filter((thread) =>
        threadNeedsEval(thread.metadata),
    ).length;

    // Membership linkage lives in the backend `mastra_threads` table, not on
    // the Mastra thread metadata, so batch-resolve it in a few calls (CMM only).
    const isCmmAgent = agentId === CONVERSATIONAL_MM_AGENT.id;
    const threadIds = useMemo(
        () => threads.map((thread) => thread.id),
        [threads],
    );
    const { data: threadStats } = useMastraThreadStats({
        agentId,
        threadIds,
        enabled: isCmmAgent,
    });

    // Fold `membershipId` into the row objects (rather than reading `threadStats`
    // directly in the cell body) so the DataTable `value` reference changes when
    // the async stats arrive — otherwise PrimeReact keeps the cached empty cells
    // until a pagination/re-render forces them to re-evaluate.
    const rows = useMemo(
        () =>
            threads.map((thread) => ({
                ...buildThreadRow({
                    thread,
                    agentId,
                    metricFieldIds,
                    scorerIds,
                }),
                membershipId: threadStats?.[thread.id]?.membershipId ?? null,
            })),
        [threads, agentId, metricFieldIds, scorerIds, threadStats],
    );

    const renderMetricCell =
        (metricFieldId: string) =>
        (row: AiDashboardThreadRow): ReactNode => {
            if (metricFieldId === ERRORS_METRIC_ID) {
                const hasError = getThreadHasError(row.thread.metadata);
                if (hasError == null) {
                    return <MutedTableCell />;
                }

                return (
                    <BodySmallRegular
                        color={hasError ? '$red900' : undefined}
                        fontWeight={hasError ? '700' : undefined}
                    >
                        {hasError ? 'Yes' : 'No'}
                    </BodySmallRegular>
                );
            }

            const value = getThreadMetricValue(
                row.thread.metadata,
                metricFieldId,
            );
            if (value == null || value === '') {
                return <MutedTableCell />;
            }

            return (
                <BodySmallRegular>
                    {formatThreadMetricFieldValue(metricFieldId, value)}
                </BodySmallRegular>
            );
        };

    const renderScorerCell =
        (scorerId: string) =>
        (row: AiDashboardThreadRow): ReactNode => {
            const result = getThreadScorerResult(row.thread.metadata, scorerId);
            if (result == null) {
                return <MutedTableCell />;
            }

            return (
                <ScorerValueWithRubric score={result.score} scorerId={scorerId}>
                    <BodySmallRegular
                        color={getScorerValueColor({
                            score: result.score,
                            scorerId,
                        })}
                        fontWeight="700"
                    >
                        {formatScorerValue({ score: result.score, scorerId })}
                    </BodySmallRegular>
                </ScorerValueWithRubric>
            );
        };

    const renderThreadId = (row: AiDashboardThreadRow) => (
        <BodySmallRegular style={{ fontFamily: 'monospace', fontSize: 12 }}>
            {row.id}
        </BodySmallRegular>
    );

    const renderResourceId = (row: AiDashboardThreadRow) =>
        row.resourceId === '' ? (
            <MutedTableCell />
        ) : (
            <BodySmallRegular style={{ fontFamily: 'monospace', fontSize: 12 }}>
                {row.resourceId}
            </BodySmallRegular>
        );

    const renderMetricsCapturedAt = (row: AiDashboardThreadRow) => {
        const lastCapturedAt = row.metricsCapturedAt;
        return lastCapturedAt == null ? (
            <MutedTableCell />
        ) : (
            <BodySmallRegular>
                {formatToSimpleDateTimeString(lastCapturedAt)}
            </BodySmallRegular>
        );
    };

    const renderScoredAt = (row: AiDashboardThreadRow) => {
        const lastScoredAt = row.scoredAt;
        return lastScoredAt == null ? (
            <MutedTableCell />
        ) : (
            <BodySmallRegular>
                {formatToSimpleDateTimeString(lastScoredAt)}
            </BodySmallRegular>
        );
    };

    const renderUpdated = (row: AiDashboardThreadRow) => (
        <BodySmallRegular>
            {formatToSimpleDateTimeString(row.updatedAt)}
        </BodySmallRegular>
    );

    const renderMembershipId = (row: AiDashboardThreadRow): ReactNode => {
        const membershipId = row.membershipId;
        if (membershipId == null || membershipId === '') {
            return <MutedTableCell />;
        }

        return (
            <XStack alignItems="center" justifyContent="center" gap={6}>
                <BodySmallRegular
                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                >
                    {membershipId}
                </BodySmallRegular>
                <IconButton
                    variant="tertiary"
                    icon={ExternalLinkIcon}
                    onPress={(event) => {
                        event.stopPropagation();
                        router.push({
                            pathname: '/(app)/membership/[id]',
                            params: { id: membershipId },
                        });
                    }}
                />
            </XStack>
        );
    };

    const renderLogRocket = (row: AiDashboardThreadRow) => {
        // Base resource ID is either a visitorId or userId.
        const url = logRocketSessionsUrl({
            userId: row.resourceId,
            platform: 'web',
        });

        return (
            <XStack justifyContent="center" alignItems="center">
                <IconButton
                    variant="tertiary"
                    icon={ExternalLinkIcon}
                    onPress={(event) => {
                        event.stopPropagation();
                        window.open(url, '_blank');
                    }}
                />
            </XStack>
        );
    };

    return (
        <YStack gap={12} width="100%" flex={1} minHeight={0} overflow="visible">
            <XStack alignItems="center" justifyContent="space-between" gap={12}>
                <XStack alignItems="center" gap={12} flex={1}>
                    <H6Emphasized>Threads</H6Emphasized>
                    <Input
                        size="tiny"
                        fontWeight="regular"
                        width={340}
                        placeholder={
                            isCmmAgent
                                ? 'Search by thread, membership, or resource ID'
                                : 'Search by thread or resource ID'
                        }
                        leftIcon={SearchIcon}
                        value={globalSearch}
                        onChangeText={onGlobalSearchChange}
                    />
                </XStack>
                <XStack alignItems="center" gap={12}>
                    <RefreshButton
                        isRefreshing={isRefreshing}
                        onRefresh={onRefresh}
                    />
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.SMALL}
                        width="auto"
                        text={`Eval unevaluated (${unevaluatedCount})`}
                        disabled={isBulkEvalPending}
                        icon={isBulkEvalPending ? <Spinner /> : undefined}
                        onPress={() => {
                            bulkEval({ agentId });
                        }}
                    />
                    {agentId === SPROUT_AGENT.id ? (
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            width="auto"
                            text="Dataset"
                            onPress={() => {
                                router.push({
                                    pathname: '/dataset/[agentId]',
                                    params: { agentId },
                                });
                            }}
                        />
                    ) : null}
                    {qaDashboardPath != null ? (
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.SMALL}
                            width="auto"
                            text="QA"
                            onPress={() => {
                                router.push(qaDashboardPath);
                            }}
                        />
                    ) : null}
                </XStack>
            </XStack>
            <YStack flex={1} minHeight={0} opacity={isRefreshing ? 0.5 : 1}>
                <DataTable
                    value={rows}
                    dataKey="id"
                    size="small"
                    showGridlines
                    stripedRows
                    scrollable
                    scrollHeight="flex"
                    tableStyle={{ width: '100%' }}
                    emptyMessage="No conversations found."
                    rowClassName={() => 'ai-dashboard-thread-row'}
                    paginator
                    rows={50}
                    rowsPerPageOptions={[25, 50, 100]}
                    sortMode="single"
                    removableSort
                    sortField="updatedAt"
                    sortOrder={-1}
                    filterDisplay="menu"
                    filters={filters}
                    onFilter={onFilter}
                    globalFilterFields={
                        isCmmAgent
                            ? ['id', 'resourceId', 'membershipId']
                            : ['id', 'resourceId']
                    }
                    onRowClick={(event: DataTableRowClickEvent) => {
                        const rowData: unknown = event.data;
                        if (!hasThreadId(rowData)) {
                            return;
                        }

                        router.push({
                            pathname: '/ai/threads/[threadId]',
                            params: { threadId: rowData.id, agentId },
                        });
                    }}
                >
                    <Column
                        field="id"
                        header="Thread ID"
                        body={renderThreadId}
                        sortable
                        filter
                        filterMatchMode={FilterMatchMode.EQUALS}
                        showFilterMatchModes={false}
                        showFilterOperator={false}
                        showAddButton={false}
                        filterPlaceholder="Thread ID"
                        style={{ minWidth: 170 }}
                    />
                    <Column
                        field="resourceId"
                        header="Resource ID"
                        body={renderResourceId}
                        sortable
                        filter
                        filterMatchMode={FilterMatchMode.EQUALS}
                        showFilterMatchModes={false}
                        showFilterOperator={false}
                        showAddButton={false}
                        filterPlaceholder="Resource ID"
                        style={{ minWidth: 170 }}
                    />
                    {isCmmAgent ? (
                        <Column
                            field="membershipId"
                            header="Membership ID"
                            body={renderMembershipId}
                            style={{ minWidth: 170, textAlign: 'center' }}
                        />
                    ) : null}
                    {metricFieldIds.map((metricFieldId) => {
                        const filterOptions =
                            getMetricColumnFilterOptions(metricFieldId);

                        return (
                            <Column
                                key={metricFieldId}
                                field={`metrics.${metricFieldId}`}
                                header={getThreadMetricDisplayName(
                                    metricFieldId,
                                )}
                                body={renderMetricCell(metricFieldId)}
                                sortable
                                filter={filterOptions != null}
                                showFilterMatchModes={false}
                                showFilterOperator={false}
                                showAddButton={false}
                                filterElement={
                                    filterOptions != null
                                        ? (options) => (
                                              <ColumnDropdownFilter
                                                  options={options}
                                                  filterOptions={filterOptions}
                                              />
                                          )
                                        : undefined
                                }
                                style={{ width: 112, textAlign: 'center' }}
                            />
                        );
                    })}
                    <Column
                        field="metricsCapturedAt"
                        header="Metrics Captured At"
                        body={renderMetricsCapturedAt}
                        sortable
                        style={{ width: 160 }}
                    />
                    {scorerIds.map((scorerId) => {
                        const isBinaryScorer = isBinaryThreadScorer(scorerId);

                        return (
                            <Column
                                key={scorerId}
                                field={`scorers.${scorerId}`}
                                header={getScorerDisplayName(
                                    scorerDisplayNames,
                                    scorerId,
                                )}
                                body={renderScorerCell(scorerId)}
                                sortable
                                filter={isBinaryScorer}
                                showFilterMatchModes={false}
                                showFilterOperator={false}
                                showAddButton={false}
                                filterElement={
                                    isBinaryScorer
                                        ? (options) => (
                                              <ColumnDropdownFilter
                                                  options={options}
                                                  filterOptions={
                                                      BINARY_SCORER_FILTER_OPTIONS
                                                  }
                                              />
                                          )
                                        : undefined
                                }
                                style={{ width: 96, textAlign: 'center' }}
                            />
                        );
                    })}
                    <Column
                        field="scoredAt"
                        header="Scored At"
                        body={renderScoredAt}
                        sortable
                        style={{ width: 160 }}
                    />
                    <Column
                        field="updatedAt"
                        header="Updated At"
                        body={renderUpdated}
                        sortable
                        style={{ width: 160 }}
                    />
                    {showLogRocketColumn ? (
                        <Column
                            field="logRocket"
                            header="LogRocket"
                            body={renderLogRocket}
                            style={{ width: 72, textAlign: 'center' }}
                        />
                    ) : null}
                </DataTable>
            </YStack>
            <style>{`
                .p-datatable .ai-dashboard-thread-row {
                    cursor: pointer;
                }
                .p-datatable .ai-dashboard-thread-row:hover {
                    background-color: #E1FDEA !important;
                }
            `}</style>
        </YStack>
    );
};

const RefreshButton = ({
    isRefreshing,
    onRefresh,
}: {
    isRefreshing: boolean;
    onRefresh: () => void;
}) => {
    const [refreshed, setRefreshed] = useState(false);

    useEffect(() => {
        setRefreshed(!isRefreshing);
        if (!isRefreshing) {
            setTimeout(() => {
                setRefreshed(false);
            }, 1000);
        }
    }, [isRefreshing]);

    return (
        <>
            {refreshed && (
                <XStack
                    animation="slow"
                    animateOnly={['opacity']}
                    enterStyle={{ opacity: 0 }}
                    exitStyle={{ opacity: 0 }}
                >
                    <BadgeLabel
                        text="Refreshed"
                        variant="positive"
                        size="regular"
                    />
                </XStack>
            )}
            {isRefreshing ? (
                <XStack
                    width={40}
                    height={40}
                    alignItems="center"
                    justifyContent="center"
                >
                    <Spinner />
                </XStack>
            ) : (
                <IconButton
                    variant="secondary"
                    icon={RefreshCwIcon}
                    onPress={onRefresh}
                />
            )}
        </>
    );
};
